import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // ← import navigation
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  User,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Eye,
} from "lucide-react";
import { showToastNotification } from "../../utility/helper";
import { Button } from "../../components/ui/button";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import TablePagination from "../../components/common/TablePagination";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import { RoutesLink } from "@/utility/RouteLinks";
import type { IFilter } from "@/components/common/CommonFilter";
import type { IPageInfo } from "@/interface/common.interface";
import CommonFilter from "@/components/common/CommonFilter";

interface ISupervisor {
  id: string;
  name: string;
  email: string;
  phone: string;
  department: string;
  site: string;
  status: "active" | "inactive";
}

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const Supervisor: React.FC = () => {
  const navigate = useNavigate(); // ← navigation hook

  const [isLoading, setIsLoading] = useState(false);
  const [supervisorList, setSupervisorList] = useState<ISupervisor[]>([]);

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    status: "",
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchSupervisors = async () => {
    setIsLoading(true);
    try {
      // Mock data – replace with API
      const mockData: ISupervisor[] = [
        {
          id: "s1",
          name: "Rahul Singh",
          email: "rahul@example.com",
          phone: "9876543210",
          department: "Civil",
          site: "Site 1 — Foundation",
          status: "active",
        },
        {
          id: "s2",
          name: "Priya Patel",
          email: "priya@example.com",
          phone: "9876543211",
          department: "Electrical",
          site: "Site 4 — Roofing",
          status: "active",
        },
        {
          id: "s3",
          name: "Amit Kumar",
          email: "amit@example.com",
          phone: "9876543212",
          department: "Plumbing",
          site: "Site 2 — Block A",
          status: "inactive",
        },
      ];
      setSupervisorList(mockData);
      setPaginationData((prev) => ({
        ...prev,
        total: mockData.length,
        totalPages: 1,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSupervisors();
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const handleSearchChange = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const openAddPage = () => {
    navigate(RoutesLink.createSupervisor);
  };

  const openEditPage = (supervisor: ISupervisor) => {
    navigate(`${RoutesLink.editSupervisor}/${supervisor.id}`);
  };

  // NOTE: add `supervisorDetails` to RoutesLink if it isn't there yet,
  // e.g. supervisorDetails: "/supervisors/details"
  const openDetailsPage = (supervisor: ISupervisor) => {
    navigate(`${RoutesLink.supervisorDetail}/${supervisor.id}`);
  };

  const columns: TableColumn<ISupervisor>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item, index) => (
        <span className="text-muted-foreground font-medium text-sm">
          {(paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1}
        </span>
      ),
    },
    {
      key: "name",
      header: "Name",
      render: (item) => (
        <button
          onClick={() => openDetailsPage(item)}
          className="flex items-center gap-3 text-left hover:opacity-80 transition"
        >
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <User className="h-4 w-4" />
          </div>
          <span className="font-semibold text-gray-700">{item.name}</span>
        </button>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (item) => (
        <div className="flex items-center gap-1.5 text-gray-600">
          <Mail className="h-3.5 w-3.5" />
          <span>{item.email}</span>
        </div>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      render: (item) => (
        <div className="flex items-center gap-1.5 text-gray-600">
          <Phone className="h-3.5 w-3.5" />
          <span>{item.phone}</span>
        </div>
      ),
    },
    {
      key: "site",
      header: "Site",
      render: (item) => (
        <div className="flex items-center gap-1.5 text-gray-600">
          <MapPin className="h-3.5 w-3.5" />
          <span>{item.site}</span>
        </div>
      ),
    },
    {
      key: "department",
      header: "Department",
      render: (item) => (
        <span className="text-gray-700">{item.department}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${
            item.status === "active"
              ? "text-green-600 border-green-600 bg-green-50"
              : "text-red-600 border-red-600 bg-red-50"
          }`}
        >
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() => openDetailsPage(item)}
              className="cursor-pointer"
            >
              <Eye className="h-4 w-4 mr-2" /> View
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => openEditPage(item)}
              className="cursor-pointer"
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                showToastNotification("info", "Delete not implemented yet");
              }}
              className="cursor-pointer text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <MainHeading title="Supervisor Management" />
        <Button onClick={openAddPage} className="gap-2 w-full sm:w-auto">
          <Plus size={16} /> Add Supervisor
        </Button>
      </div>

      <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
        <CommonFilter
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
          value={filter}
          filterOptions={STATUS_OPTIONS}
          filterName="status"
          filterPlaceholder="Select Status"
          onChange={handleFilterChange}
        />
        <CommonTable
          data={supervisorList}
          columns={columns}
          loading={isLoading}
          stickyColumns={2}
        />
        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default Supervisor;
